import { type ErrorCode } from '@neogeniux/api-contracts';
import { i18n } from '@i18n/translations';

/**
 * Error code to i18n translation key mapping
 * This maps backend error codes to frontend translation keys
 */
/**
 * Reads the backend errorCode off either the ApiError top level or its nested
 * `details` body — the two shapes `api.service.createApiError` can produce.
 */
export const getErrorCode = (err: unknown): string | undefined => {
  const rec = err as
    | { errorCode?: string; details?: { errorCode?: string } }
    | null
    | undefined;
  return rec?.errorCode ?? rec?.details?.errorCode;
};

export const errorCodeMap: Partial<Record<ErrorCode, string>> = {
  // Platform-fiscal comprobantes — sadmin issuing DGII comprobantes to tenants
  PLATFORM_FISCAL_CREDIT_BLOCKED:
    i18n.HTTP_ERRORS.PLATFORM_FISCAL_CREDIT_BLOCKED,
  PLATFORM_FISCAL_NO_SEQUENCE: i18n.HTTP_ERRORS.PLATFORM_FISCAL_NO_SEQUENCE,
  PLATFORM_FISCAL_CREDIT_NOTE_NO_SEQUENCE:
    i18n.HTTP_ERRORS.PLATFORM_FISCAL_CREDIT_NOTE_NO_SEQUENCE,
  PLATFORM_FISCAL_NO_TAX_DEFINITION:
    i18n.HTTP_ERRORS.PLATFORM_FISCAL_NO_TAX_DEFINITION,
  // Restaurant tab-item void manager-approval gate
  RESTAURANT_TAB_ITEM_VOID_APPROVAL_REQUIRED:
    i18n.HTTP_ERRORS.RESTAURANT_TAB_ITEM_VOID_APPROVAL_REQUIRED,
  RESTAURANT_VOID_APPROVAL_INVALID:
    i18n.HTTP_ERRORS.RESTAURANT_VOID_APPROVAL_INVALID,
  // Sadmin billing — "Ajustar y liquidar" orchestration preconditions + failure
  NO_RESELLER_ASSIGNMENT: i18n.HTTP_ERRORS.NO_RESELLER_ASSIGNMENT,
  CURRENCY_UNRESOLVED: i18n.HTTP_ERRORS.CURRENCY_UNRESOLVED,
  INVOICE_NOT_VOIDABLE: i18n.HTTP_ERRORS.INVOICE_NOT_VOIDABLE,
  CONFLICTING_ACTIVE_ANNUAL_SUB: i18n.HTTP_ERRORS.CONFLICTING_ACTIVE_ANNUAL_SUB,
  ADJUST_SETTLE_STEP_FAILED: i18n.HTTP_ERRORS.ADJUST_SETTLE_STEP_FAILED,
  ZERO_COMMISSION_BALANCE: i18n.HTTP_ERRORS.ZERO_COMMISSION_BALANCE,
  ADJUST_SETTLE_PREVIEW_STALE: i18n.HTTP_ERRORS.ADJUST_SETTLE_PREVIEW_STALE,
  RESELLER_ACCRUAL_NOT_SETTLEABLE:
    i18n.HTTP_ERRORS.RESELLER_ACCRUAL_NOT_SETTLEABLE,
  // Inventory
  CATEGORY_GENERIC_PROTECTED: i18n.HTTP_ERRORS.CATEGORY_GENERIC_PROTECTED,
  // Bulk import — clients + providers (with opening balance)
  CUSTOMER_BULK_IMPORT_ROW_DUPLICATE: i18n.BULK_ENTITY_IMPORT.ERR_ROW_DUPLICATE,
  CUSTOMER_OPENING_BALANCE_REFERENCE_REQUIRED:
    i18n.BULK_ENTITY_IMPORT.ERR_OPENING_BALANCE_REFERENCE_REQUIRED,
  PROVIDER_BULK_IMPORT_ROW_DUPLICATE: i18n.BULK_ENTITY_IMPORT.ERR_ROW_DUPLICATE,
  PROVIDER_OPENING_BALANCE_REFERENCE_REQUIRED:
    i18n.BULK_ENTITY_IMPORT.ERR_OPENING_BALANCE_REFERENCE_REQUIRED,
  // Authentication errors
  RENTAL_FIANZA_EXCEEDS_TOTAL: i18n.HTTP_ERRORS.RENTAL_FIANZA_EXCEEDS_TOTAL,
  RENTAL_CANCEL_REQUIRES_CREDIT_NOTE:
    i18n.HTTP_ERRORS.RENTAL_CANCEL_REQUIRES_CREDIT_NOTE,
  RENTAL_LOCKED_IN_PRODUCTION: i18n.HTTP_ERRORS.RENTAL_LOCKED_IN_PRODUCTION,
  RENTAL_LOCKED_PAID: i18n.HTTP_ERRORS.RENTAL_LOCKED_PAID,
  RENTAL_PICKUP_NO_EMAIL: i18n.HTTP_ERRORS.RENTAL_PICKUP_NO_EMAIL,
  RENTAL_PICKUP_NO_PHONE: i18n.HTTP_ERRORS.RENTAL_PICKUP_NO_PHONE,
  RENTAL_PICKUP_TOKEN_EXPIRED: i18n.HTTP_ERRORS.RENTAL_PICKUP_TOKEN_EXPIRED,
  RENTAL_PICKUP_TOKEN_INVALID: i18n.HTTP_ERRORS.RENTAL_PICKUP_TOKEN_INVALID,
  RENTAL_NO_ACTIVE_SHIFT: i18n.HTTP_ERRORS.RENTAL_NO_ACTIVE_SHIFT,
  RENTAL_ALREADY_INVOICED: i18n.HTTP_ERRORS.RENTAL_ALREADY_INVOICED,
  RENTAL_ALREADY_RETURNED: i18n.HTTP_ERRORS.RENTAL_ALREADY_RETURNED,
  RENTAL_AVAILABILITY_CONFLICT: i18n.HTTP_ERRORS.RENTAL_AVAILABILITY_CONFLICT,
  RENTAL_COMMANDA_STARTED: i18n.HTTP_ERRORS.RENTAL_COMMANDA_STARTED,
  RENTAL_DELIVERY_REQUIRES_FULL_PAYMENT:
    i18n.HTTP_ERRORS.RENTAL_DELIVERY_REQUIRES_FULL_PAYMENT,
  RENTAL_ABONO_PENDING: i18n.HTTP_ERRORS.RENTAL_ABONO_PENDING,
  RENTAL_ADD_ITEMS_INVALID_STATE:
    i18n.HTTP_ERRORS.RENTAL_ADD_ITEMS_INVALID_STATE,
  RENTAL_ADD_ITEMS_LOCKED_INVOICED:
    i18n.HTTP_ERRORS.RENTAL_ADD_ITEMS_LOCKED_INVOICED,
  RENTAL_ADD_ITEMS_EMPTY: i18n.HTTP_ERRORS.RENTAL_ADD_ITEMS_EMPTY,
  RENTAL_FIANZA_MISMATCH: i18n.HTTP_ERRORS.RENTAL_FIANZA_MISMATCH,
  RENTAL_FIANZA_REQUIRED: i18n.HTTP_ERRORS.RENTAL_FIANZA_REQUIRED,
  RENTAL_INVALID_TRANSITION: i18n.HTTP_ERRORS.RENTAL_INVALID_TRANSITION,
  RENTAL_LATE_REFUND_FORBIDDEN: i18n.HTTP_ERRORS.RENTAL_LATE_REFUND_FORBIDDEN,
  RENTAL_NOT_FOUND: i18n.HTTP_ERRORS.RENTAL_NOT_FOUND,
  RENTAL_NOT_READY_FOR_INVOICE: i18n.HTTP_ERRORS.RENTAL_NOT_READY_FOR_INVOICE,
  RENTAL_RESERVATION_ALREADY_CONVERTED:
    i18n.HTTP_ERRORS.RENTAL_RESERVATION_ALREADY_CONVERTED,
  RENTAL_RESERVATION_EXPIRED: i18n.HTTP_ERRORS.RENTAL_RESERVATION_EXPIRED,
  RENTAL_RESERVATION_NOT_FOUND: i18n.HTTP_ERRORS.RENTAL_RESERVATION_NOT_FOUND,
  RENTAL_RETURN_DATE_REQUIRED: i18n.HTTP_ERRORS.RENTAL_RETURN_DATE_REQUIRED,
  RENTAL_STOCK_INSUFFICIENT: i18n.HTTP_ERRORS.RENTAL_STOCK_INSUFFICIENT,
  RENTAL_ZERO_PRICE_FORBIDDEN: i18n.HTTP_ERRORS.RENTAL_ZERO_PRICE_FORBIDDEN,
  RENTAL_CUSTOMER_REQUIRED: i18n.HTTP_ERRORS.RENTAL_CUSTOMER_REQUIRED,
  RENTAL_SETTLEMENT_UNRESOLVED: i18n.HTTP_ERRORS.RENTAL_SETTLEMENT_UNRESOLVED,
  RENTAL_NO_REFUNDABLE_DEPOSIT: i18n.HTTP_ERRORS.RENTAL_NO_REFUNDABLE_DEPOSIT,
  RENTAL_DAMAGE_ADJUST_NOT_PENDING:
    i18n.HTTP_ERRORS.RENTAL_DAMAGE_ADJUST_NOT_PENDING,
  RENTAL_DAMAGE_ADJUST_INVALID_AMOUNT:
    i18n.HTTP_ERRORS.RENTAL_DAMAGE_ADJUST_INVALID_AMOUNT,
  INVALID_CREDENTIALS: i18n.HTTP_ERRORS.AUTHENTICATION_FAILED,
  EMAIL_NOT_VERIFIED: i18n.HTTP_ERRORS.EMAIL_NOT_VERIFIED,
  ACCOUNT_LOCKED: i18n.HTTP_ERRORS.ACCOUNT_LOCKED,
  ACCOUNT_DISABLED: i18n.HTTP_ERRORS.ACCOUNT_DISABLED,
  USER_NOT_ASSOCIATED_WITH_BUSINESS:
    i18n.HTTP_ERRORS.USER_NOT_ASSOCIATED_WITH_BUSINESS,
  ACCOUNT_NOT_FULLY_ACTIVATED: i18n.HTTP_ERRORS.ACCOUNT_NOT_FULLY_ACTIVATED,
  REFRESH_TOKEN_EXPIRED: i18n.HTTP_ERRORS.REFRESH_TOKEN_EXPIRED,
  REFRESH_TOKEN_REVOKED: i18n.HTTP_ERRORS.REFRESH_TOKEN_REVOKED,
  REFRESH_TOKEN_REUSE_DETECTED: i18n.HTTP_ERRORS.REFRESH_TOKEN_REUSE_DETECTED,
  SESSION_LIMIT_EXCEEDED: i18n.HTTP_ERRORS.SESSION_LIMIT_EXCEEDED,
  UNAUTHORIZED: i18n.HTTP_ERRORS.AUTHENTICATION_FAILED,
  REQUIRES_ONBOARDING: i18n.HTTP_ERRORS.REQUIRES_ONBOARDING,
  SUBSCRIPTION_CANCELLED: i18n.HTTP_ERRORS.SUBSCRIPTION_CANCELLED,
  SUBSCRIPTION_PAST_DUE: i18n.HTTP_ERRORS.SUBSCRIPTION_PAST_DUE,
  RULES_ENGINE_UNBALANCED: i18n.HTTP_ERRORS.RULES_ENGINE_UNBALANCED,
  RECONCILE_LINES_UNBALANCED: i18n.HTTP_ERRORS.RECONCILE_LINES_UNBALANCED,
  RECONCILE_ACCOUNT_INVALID: i18n.HTTP_ERRORS.RECONCILE_ACCOUNT_INVALID,
  PARKED_EVENT_NOT_UNBALANCED: i18n.HTTP_ERRORS.PARKED_EVENT_NOT_UNBALANCED,
  SUBSCRIPTION_SUSPENDED: i18n.HTTP_ERRORS.SUBSCRIPTION_SUSPENDED,
  SUBSCRIPTION_INVOICE_OVERDUE: i18n.HTTP_ERRORS.SUBSCRIPTION_INVOICE_OVERDUE,
  CATALOG_SUBSCRIPTION_PAST_DUE: i18n.HTTP_ERRORS.CATALOG_SUBSCRIPTION_PAST_DUE,
  BILLING_INTERVAL_LOCKED_UNPAID_INVOICE:
    i18n.HTTP_ERRORS.BILLING_INTERVAL_LOCKED_UNPAID_INVOICE,
  BILLING_INVALID_PERIOD: i18n.HTTP_ERRORS.BILLING_INVALID_PERIOD,
  BILLING_ISSUED_AT_IN_FUTURE: i18n.HTTP_ERRORS.BILLING_ISSUED_AT_IN_FUTURE,
  INVOICE_CREDIT_TERM_UNKNOWN: i18n.HTTP_ERRORS.INVOICE_CREDIT_TERM_UNKNOWN,
  ADDON_REDUCTION_CAPACITY_IN_USE:
    i18n.HTTP_ERRORS.ADDON_REDUCTION_CAPACITY_IN_USE,
  ADDON_REDUCTION_SLOTS_INVALID: i18n.HTTP_ERRORS.ADDON_REDUCTION_SLOTS_INVALID,
  ADDON_REDUCTION_EXCEEDS_ACTIVE:
    i18n.HTTP_ERRORS.ADDON_REDUCTION_EXCEEDS_ACTIVE,
  BILLING_SUBSCRIPTION_CANCELLING:
    i18n.HTTP_ERRORS.BILLING_SUBSCRIPTION_CANCELLING,
  BILLING_COLLECTION_AUTHORITY_MISMATCH:
    i18n.HTTP_ERRORS.BILLING_COLLECTION_AUTHORITY_MISMATCH,
  BILLING_COLLECTION_SKIPPED: i18n.HTTP_ERRORS.BILLING_COLLECTION_SKIPPED,
  BILLING_INVOICE_ALREADY_PAID: i18n.HTTP_ERRORS.BILLING_INVOICE_ALREADY_PAID,
  BILLING_PAYMENT_IN_FLIGHT: i18n.HTTP_ERRORS.BILLING_PAYMENT_IN_FLIGHT,
  CHANGE_REQUEST_COMPLIANCE_BLOCKED:
    i18n.HTTP_ERRORS.CHANGE_REQUEST_COMPLIANCE_BLOCKED,
  PREPAYMENT_PROVIDER_MANAGED: i18n.HTTP_ERRORS.PREPAYMENT_PROVIDER_MANAGED,
  PREPAYMENT_OVERLAPPING_INVOICE:
    i18n.HTTP_ERRORS.PREPAYMENT_OVERLAPPING_INVOICE,
  PREPAYMENT_INVALID_MONTHS: i18n.HTTP_ERRORS.PREPAYMENT_INVALID_MONTHS,
  PREPAYMENT_SUBSCRIPTION_CANCELLING:
    i18n.HTTP_ERRORS.PREPAYMENT_SUBSCRIPTION_CANCELLING,
  PREPAYMENT_CARD_FAILED: i18n.HTTP_ERRORS.PREPAYMENT_CARD_FAILED,
  PREPAYMENT_NO_PAYMENT_METHOD: i18n.HTTP_ERRORS.PREPAYMENT_NO_PAYMENT_METHOD,
  PREPAYMENT_INVALID_MONTHS_COUNT:
    i18n.HTTP_ERRORS.PREPAYMENT_INVALID_MONTHS_COUNT,
  CREDIT_NOTE_SEQUENCE_NOT_CONFIGURED:
    i18n.HTTP_ERRORS.CREDIT_NOTE_SEQUENCE_NOT_CONFIGURED,
  CREDIT_NOTE_SEQUENCE_MISSING_ETYPE:
    i18n.HTTP_ERRORS.CREDIT_NOTE_SEQUENCE_MISSING_ETYPE,
  CREDIT_NOTE_ORIGINAL_NOT_FISCAL:
    i18n.HTTP_ERRORS.CREDIT_NOTE_ORIGINAL_NOT_FISCAL,
  CREDIT_NOTE_SEQUENCE_MISMATCH_B_TYPE:
    i18n.HTTP_ERRORS.CREDIT_NOTE_SEQUENCE_MISMATCH_B_TYPE,
  ECF_E32_LARGE_AMOUNT_REQUIRES_RNC:
    i18n.HTTP_ERRORS.ECF_E32_LARGE_AMOUNT_REQUIRES_RNC,
  // Bulk-invoice-import validator emits these on individual rows; the
  // strings are ALSO surfaced through translateValidationMessage in
  // bulk-invoice-import-modal.tsx. This map covers the fallback path
  // where the same code lands on a thrown exception (future callers).
  INVOICE_BULK_IMPORT_QUOTE_NOT_SUPPORTED_YET:
    i18n.INVOICE_BULK_IMPORT.QUOTE_NOT_SUPPORTED_YET,
  INVOICE_BULK_IMPORT_RNC_REQUIRED_FOR_FISCAL_CREDIT:
    i18n.INVOICE_BULK_IMPORT.RNC_REQUIRED_FOR_FISCAL_CREDIT,
  INVOICE_BULK_IMPORT_INVALID_TYPE: i18n.INVOICE_BULK_IMPORT.INVALID_TYPE,
  INVOICE_BULK_IMPORT_DATA_CORRUPTED:
    i18n.HTTP_ERRORS.INVOICE_BULK_IMPORT_DATA_CORRUPTED,
  INVOICE_BULK_IMPORT_CANNOT_RESUME:
    i18n.HTTP_ERRORS.INVOICE_BULK_IMPORT_CANNOT_RESUME,
  INVOICE_BULK_IMPORT_NO_ITEMS_TO_PROCESS:
    i18n.HTTP_ERRORS.INVOICE_BULK_IMPORT_NO_ITEMS_TO_PROCESS,
  CREDIT_LIMIT_REQUIRED_NOT_SET: i18n.HTTP_ERRORS.CREDIT_LIMIT_REQUIRED_NOT_SET,
  CREDIT_APPROVAL_REQUIRED: i18n.HTTP_ERRORS.CREDIT_APPROVAL_REQUIRED,
  CREDIT_APPROVAL_INVALID: i18n.HTTP_ERRORS.CREDIT_APPROVAL_INVALID,
  PRODUCTS_EDIT_FIELD_FORBIDDEN: i18n.HTTP_ERRORS.PRODUCTS_EDIT_FIELD_FORBIDDEN,
  FREE_PLAN_NON_ADMIN: i18n.HTTP_ERRORS.FREE_PLAN_NON_ADMIN,
  PLAN_CHANGE_COOLDOWN: i18n.HTTP_ERRORS.PLAN_CHANGE_COOLDOWN,
  PIN_NOT_CONFIGURED: i18n.HTTP_ERRORS.PIN_NOT_CONFIGURED,

  // Captcha gate
  CAPTCHA_REQUIRED: i18n.HTTP_ERRORS.CAPTCHA_REQUIRED,
  CAPTCHA_INVALID: i18n.HTTP_ERRORS.CAPTCHA_INVALID,

  // Trial errors
  TRIAL_EXPIRED: i18n.HTTP_ERRORS.TRIAL_EXPIRED,

  // Plan limit errors
  PREPAID_PACK_SALES_DISABLED: i18n.HTTP_ERRORS.PREPAID_PACK_SALES_DISABLED,
  PREPAID_PACK_PAYMENT_METHOD_REQUIRED:
    i18n.HTTP_ERRORS.PREPAID_PACK_PAYMENT_METHOD_REQUIRED,
  PLAN_LIMIT_INVOICES: i18n.HTTP_ERRORS.PLAN_LIMIT_INVOICES,
  PLAN_LIMIT_INVOICES_TOTAL: i18n.HTTP_ERRORS.PLAN_LIMIT_INVOICES_TOTAL,
  PLAN_LIMIT_PRODUCTS: i18n.HTTP_ERRORS.PLAN_LIMIT_PRODUCTS,
  PLAN_LIMIT_USERS: i18n.HTTP_ERRORS.PLAN_LIMIT_USERS,
  PLAN_LIMIT_WAREHOUSES: i18n.HTTP_ERRORS.PLAN_LIMIT_WAREHOUSES,
  BRANCH_EMPTY_REQUIRES_OWN_WAREHOUSE:
    i18n.HTTP_ERRORS.BRANCH_EMPTY_REQUIRES_OWN_WAREHOUSE,
  BRANCH_DELETE_HEADQUARTERS: i18n.HTTP_ERRORS.BRANCH_DELETE_HEADQUARTERS,
  BRANCH_DELETE_LAST_BRANCH: i18n.HTTP_ERRORS.BRANCH_DELETE_LAST_BRANCH,
  BRANCH_DELETE_INVALID_TARGET: i18n.HTTP_ERRORS.BRANCH_DELETE_INVALID_TARGET,
  BRANCH_DELETE_CONFIRMATION_MISMATCH:
    i18n.HTTP_ERRORS.BRANCH_DELETE_CONFIRMATION_MISMATCH,
  BRANCH_DELETE_BLOCKED: i18n.HTTP_ERRORS.BRANCH_DELETE_BLOCKED,
  BRANCH_DELETED: i18n.HTTP_ERRORS.BRANCH_DELETED,
  PLAN_LIMIT_BRANCHES: i18n.HTTP_ERRORS.PLAN_LIMIT_BRANCHES,
  PLAN_LIMIT_KDS_NOT_AVAILABLE: i18n.HTTP_ERRORS.PLAN_LIMIT_KDS_NOT_AVAILABLE,
  KDS_NODE_HAS_LIVE_ORDERS: i18n.HTTP_ERRORS.KDS_NODE_HAS_LIVE_ORDERS,
  KDS_NODE_NOT_ENTRY: i18n.HTTP_ERRORS.KDS_NODE_NOT_ENTRY,
  KDS_NODE_CYCLE_DETECTED: i18n.HTTP_ERRORS.KDS_NODE_CYCLE_DETECTED,
  KDS_READER_CANNOT_MUTATE: i18n.HTTP_ERRORS.KDS_READER_CANNOT_MUTATE,
  RESTAURANT_ENVIRONMENT_HAS_OPEN_ACCOUNT:
    i18n.backend.RESTAURANT_ENVIRONMENT_HAS_OPEN_ACCOUNT,
  RESTAURANT_TABLE_HAS_OPEN_ACCOUNT:
    i18n.backend.RESTAURANT_TABLE_HAS_OPEN_ACCOUNT,

  // Cost management errors
  PRODUCT_COST_REQUIRED: i18n.HTTP_ERRORS.PRODUCT_COST_REQUIRED,
  PRODUCT_COST_EDIT_FORBIDDEN: i18n.HTTP_ERRORS.PRODUCT_COST_EDIT_FORBIDDEN,
  PRODUCT_SKU_DUPLICATE: i18n.HTTP_ERRORS.PRODUCT_SKU_DUPLICATE,
  PRODUCT_VARIANT_IN_RECIPE: i18n.HTTP_ERRORS.PRODUCT_VARIANT_IN_RECIPE,
  PRODUCT_PRESENTATION_DUPLICATE:
    i18n.HTTP_ERRORS.PRODUCT_PRESENTATION_DUPLICATE,
  SALES_VENDOR_NAME_REQUIRED: i18n.HTTP_ERRORS.SALES_VENDOR_NAME_REQUIRED,
  SALES_VENDOR_DUPLICATE_NAME: i18n.HTTP_ERRORS.SALES_VENDOR_DUPLICATE_NAME,
  SALES_VENDOR_NOT_FOUND: i18n.HTTP_ERRORS.SALES_VENDOR_NOT_FOUND,
  SALES_VENDOR_NOT_ASSIGNABLE_TO_BRANCH:
    i18n.HTTP_ERRORS.SALES_VENDOR_NOT_ASSIGNABLE_TO_BRANCH,
  SALES_VENDOR_INACTIVE: i18n.HTTP_ERRORS.SALES_VENDOR_INACTIVE,
  PURCHASE_ORDER_CANCELLED_RECEIVE:
    i18n.HTTP_ERRORS.PURCHASE_ORDER_CANCELLED_RECEIVE,
  PRODUCT_VARIANT_PARENT_NOT_TRANSACTABLE:
    i18n.HTTP_ERRORS.PRODUCT_VARIANT_PARENT_NOT_TRANSACTABLE,

  // Registration errors
  EMAIL_ALREADY_REGISTERED: i18n.HTTP_ERRORS.EMAIL_ALREADY_REGISTERED,
  USER_EMAIL_ALREADY_EXISTS: i18n.HTTP_ERRORS.USER_EMAIL_ALREADY_EXISTS,
  USER_USERNAME_ALREADY_EXISTS: i18n.HTTP_ERRORS.USER_USERNAME_ALREADY_EXISTS,
  INVALID_VERIFICATION_TOKEN: i18n.HTTP_ERRORS.INVALID_VERIFICATION_TOKEN,
  VERIFICATION_TOKEN_EXPIRED: i18n.HTTP_ERRORS.VERIFICATION_TOKEN_EXPIRED,
  ACCOUNT_ALREADY_VERIFIED: i18n.HTTP_ERRORS.ACCOUNT_ALREADY_VERIFIED,
  ACCOUNT_EXISTS_WITH_GOOGLE: i18n.HTTP_ERRORS.ACCOUNT_EXISTS_WITH_GOOGLE,
  ACCOUNT_EXISTS_WITH_EMAIL: i18n.HTTP_ERRORS.ACCOUNT_EXISTS_WITH_EMAIL,
  ACCOUNT_EMAIL_ALREADY_EXISTS: i18n.HTTP_ERRORS.ACCOUNT_EMAIL_ALREADY_EXISTS,
  GOOGLE_ACCOUNT_NOT_REGISTERED: i18n.HTTP_ERRORS.GOOGLE_ACCOUNT_NOT_REGISTERED,

  // Onboarding errors
  INVALID_ONBOARDING_TOKEN: i18n.HTTP_ERRORS.INVALID_ONBOARDING_TOKEN,
  ONBOARDING_TOKEN_EXPIRED: i18n.HTTP_ERRORS.ONBOARDING_TOKEN_EXPIRED,
  ONBOARDING_ALREADY_COMPLETED: i18n.HTTP_ERRORS.ONBOARDING_ALREADY_COMPLETED,
  ONBOARDING_PASSWORD_INVALID: i18n.HTTP_ERRORS.ONBOARDING_PASSWORD_INVALID,

  // Password reset errors
  INVALID_RESET_TOKEN: i18n.HTTP_ERRORS.INVALID_RESET_TOKEN,
  RESET_TOKEN_EXPIRED: i18n.HTTP_ERRORS.RESET_TOKEN_EXPIRED,
  RESET_TOKEN_ALREADY_USED: i18n.HTTP_ERRORS.RESET_TOKEN_ALREADY_USED,

  // NCF errors
  NCF_SEQUENCE_DUPLICATE: i18n.HTTP_ERRORS.NCF_SEQUENCE_DUPLICATE,
  NCF_SEQUENCE_EXHAUSTED: i18n.HTTP_ERRORS.NCF_SEQUENCE_EXHAUSTED,
  NCF_RANGE_OVERLAP: i18n.HTTP_ERRORS.NCF_RANGE_OVERLAP,
  NCF_NOT_CONFIGURED: i18n.HTTP_ERRORS.NCF_NOT_CONFIGURED,
  NCF_RANGE_IN_USE: i18n.HTTP_ERRORS.NCF_RANGE_IN_USE,
  NCF_SPLIT_INVALID_TILING: i18n.HTTP_ERRORS.NCF_SPLIT_INVALID_TILING,

  // Invoice free-items gate
  INVOICE_FREE_ITEMS_NOT_ALLOWED:
    i18n.HTTP_ERRORS.INVOICE_FREE_ITEMS_NOT_ALLOWED,
  INVOICE_FREE_ITEM_MISSING_DESCRIPTION:
    i18n.HTTP_ERRORS.INVOICE_FREE_ITEM_MISSING_DESCRIPTION,
  INVOICE_FREE_ITEM_INVALID_PRICE:
    i18n.HTTP_ERRORS.INVOICE_FREE_ITEM_INVALID_PRICE,
  INVOICE_ITEM_PRICE_BELOW_MIN: i18n.HTTP_ERRORS.INVOICE_ITEM_PRICE_BELOW_MIN,
  DOCUMENT_BACKDATE_NOT_ALLOWED: i18n.HTTP_ERRORS.DOCUMENT_BACKDATE_NOT_ALLOWED,

  // Accounting — period lifecycle + journal entry guards
  ACCOUNTING_PERIOD_LOCKED: i18n.HTTP_ERRORS.ACCOUNTING_PERIOD_LOCKED,
  ACCOUNTING_PERIOD_NOT_OPEN: i18n.HTTP_ERRORS.ACCOUNTING_PERIOD_NOT_OPEN,
  ACCOUNTING_PERIOD_NOT_CLOSED_FOR_ADJUSTMENT:
    i18n.HTTP_ERRORS.ACCOUNTING_PERIOD_NOT_CLOSED_FOR_ADJUSTMENT,
  ACCOUNTING_PERIOD_NOT_CLOSED: i18n.HTTP_ERRORS.ACCOUNTING_PERIOD_NOT_CLOSED,
  ACCOUNTING_PERIOD_ALREADY_LOCKED:
    i18n.HTTP_ERRORS.ACCOUNTING_PERIOD_ALREADY_LOCKED,
  ACCOUNTING_PERIOD_NOT_LOCKED: i18n.HTTP_ERRORS.ACCOUNTING_PERIOD_NOT_LOCKED,
  ACCOUNTING_LOCK_MODE_STRICT: i18n.HTTP_ERRORS.ACCOUNTING_LOCK_MODE_STRICT,
  ACCOUNTING_UNLOCK_REASON_REQUIRED:
    i18n.HTTP_ERRORS.ACCOUNTING_UNLOCK_REASON_REQUIRED,
  ACCOUNTING_ADJUSTMENT_NOT_PERMITTED:
    i18n.HTTP_ERRORS.ACCOUNTING_ADJUSTMENT_NOT_PERMITTED,
  PRODUCT_IMAGE_MANAGE_NOT_PERMITTED:
    i18n.HTTP_ERRORS.PRODUCT_IMAGE_MANAGE_NOT_PERMITTED,
  ACCOUNTING_GRACE_DAYS_INVALID: i18n.HTTP_ERRORS.ACCOUNTING_GRACE_DAYS_INVALID,
  ACCOUNTING_LOCK_MODE_INVALID: i18n.HTTP_ERRORS.ACCOUNTING_LOCK_MODE_INVALID,
  FISCAL_YEAR_NOT_OPEN: i18n.HTTP_ERRORS.FISCAL_YEAR_NOT_OPEN,
  FISCAL_YEAR_NOT_CLOSED: i18n.HTTP_ERRORS.FISCAL_YEAR_NOT_CLOSED,
  FISCAL_YEAR_NO_PERIODS: i18n.HTTP_ERRORS.FISCAL_YEAR_NO_PERIODS,
  FISCAL_YEAR_CLOSING_JE_MISSING:
    i18n.HTTP_ERRORS.FISCAL_YEAR_CLOSING_JE_MISSING,
  FISCAL_YEAR_RETAINED_EARNINGS_MISSING:
    i18n.HTTP_ERRORS.FISCAL_YEAR_RETAINED_EARNINGS_MISSING,

  // Chart of accounts
  ACCOUNT_CODE_DUPLICATE: i18n.HTTP_ERRORS.ACCOUNT_CODE_DUPLICATE,

  // Backfill engine
  ACCOUNTING_NOT_ENABLED: i18n.HTTP_ERRORS.ACCOUNTING_NOT_ENABLED,
  ACCOUNTING_BACKFILL_NOT_CANCELLABLE:
    i18n.HTTP_ERRORS.ACCOUNTING_BACKFILL_NOT_CANCELLABLE,

  // Fixed assets
  FIXED_ASSET_CATEGORY_CODE_DUPLICATE:
    i18n.HTTP_ERRORS.FIXED_ASSET_CATEGORY_CODE_DUPLICATE,
  FIXED_ASSET_CATEGORY_NOT_FOUND:
    i18n.HTTP_ERRORS.FIXED_ASSET_CATEGORY_NOT_FOUND,
  FIXED_ASSET_CATEGORY_ACCOUNTS_REQUIRED:
    i18n.HTTP_ERRORS.FIXED_ASSET_CATEGORY_ACCOUNTS_REQUIRED,
  FIXED_ASSET_CATEGORY_ACCOUNTS_INCOMPLETE:
    i18n.HTTP_ERRORS.FIXED_ASSET_CATEGORY_ACCOUNTS_INCOMPLETE,
  FIXED_ASSET_CODE_DUPLICATE: i18n.HTTP_ERRORS.FIXED_ASSET_CODE_DUPLICATE,
  FIXED_ASSET_NOT_IN_SERVICE: i18n.HTTP_ERRORS.FIXED_ASSET_NOT_IN_SERVICE,
  FIXED_ASSET_TRANSFER_NO_TARGET:
    i18n.HTTP_ERRORS.FIXED_ASSET_TRANSFER_NO_TARGET,
  FIXED_ASSET_REVALUE_ZERO: i18n.HTTP_ERRORS.FIXED_ASSET_REVALUE_ZERO,
  FIXED_ASSET_DATE_INVALID: i18n.HTTP_ERRORS.FIXED_ASSET_DATE_INVALID,
  BUDGET_NAME_DUPLICATE: i18n.HTTP_ERRORS.BUDGET_NAME_DUPLICATE,
  BUDGET_NOT_DRAFT: i18n.HTTP_ERRORS.BUDGET_NOT_DRAFT,
  BUDGET_TRANSITION_INVALID: i18n.HTTP_ERRORS.BUDGET_TRANSITION_INVALID,

  // Bank account registry
  BANK_ACCOUNT_NOT_FOUND: i18n.HTTP_ERRORS.BANK_ACCOUNT_NOT_FOUND,
  BANK_ACCOUNT_ARCHIVED: i18n.HTTP_ERRORS.BANK_ACCOUNT_ARCHIVED,
  BANK_ACCOUNT_CODE_REQUIRED: i18n.HTTP_ERRORS.BANK_ACCOUNT_CODE_REQUIRED,
  BANK_ACCOUNT_NAME_REQUIRED: i18n.HTTP_ERRORS.BANK_ACCOUNT_NAME_REQUIRED,
  BANK_ACCOUNT_CODE_DUPLICATE: i18n.HTTP_ERRORS.BANK_ACCOUNT_CODE_DUPLICATE,
  BANK_ACCOUNT_GL_NOT_FOUND: i18n.HTTP_ERRORS.BANK_ACCOUNT_GL_NOT_FOUND,
  BANK_ACCOUNT_GL_NOT_POSTABLE: i18n.HTTP_ERRORS.BANK_ACCOUNT_GL_NOT_POSTABLE,
  BANK_ACCOUNT_NO_GL_LINK: i18n.HTTP_ERRORS.BANK_ACCOUNT_NO_GL_LINK,

  // Bank statement
  BANK_STATEMENT_NOT_FOUND: i18n.HTTP_ERRORS.BANK_STATEMENT_NOT_FOUND,
  BANK_STATEMENT_EMPTY: i18n.HTTP_ERRORS.BANK_STATEMENT_EMPTY,
  BANK_STATEMENT_ROW_INVALID: i18n.HTTP_ERRORS.BANK_STATEMENT_ROW_INVALID,
  BANK_STATEMENT_ROW_NO_AMOUNT: i18n.HTTP_ERRORS.BANK_STATEMENT_ROW_NO_AMOUNT,
  BANK_STATEMENT_DATE_INVALID: i18n.HTTP_ERRORS.BANK_STATEMENT_DATE_INVALID,
  BANK_STATEMENT_AMOUNT_INVALID: i18n.HTTP_ERRORS.BANK_STATEMENT_AMOUNT_INVALID,

  // Bank reconciliation session
  BANK_RECONCILIATION_ALREADY_OPEN:
    i18n.HTTP_ERRORS.BANK_RECONCILIATION_ALREADY_OPEN,
  BANK_RECON_LINE_MISMATCH: i18n.HTTP_ERRORS.BANK_RECON_LINE_MISMATCH,
  BANK_RECON_LINE_ALREADY_MATCHED:
    i18n.HTTP_ERRORS.BANK_RECON_LINE_ALREADY_MATCHED,
  BANK_RECON_LINE_NOT_MATCHED: i18n.HTTP_ERRORS.BANK_RECON_LINE_NOT_MATCHED,
  BANK_RECON_NO_STATEMENT: i18n.HTTP_ERRORS.BANK_RECON_NO_STATEMENT,
  BANK_RECON_UNMATCHED_LINES: i18n.HTTP_ERRORS.BANK_RECON_UNMATCHED_LINES,
  BANK_RECON_LOCKED: i18n.HTTP_ERRORS.BANK_RECON_LOCKED,

  // Customer (generic / system rows)
  GENERIC_CUSTOMER_READ_ONLY: i18n.HTTP_ERRORS.GENERIC_CUSTOMER_READ_ONLY,
  CUSTOMER_MERGE_INTO_SELF: i18n.HTTP_ERRORS.CUSTOMER_MERGE_INTO_SELF,
  CUSTOMER_MERGE_NOT_FOUND: i18n.HTTP_ERRORS.CUSTOMER_MERGE_NOT_FOUND,
  CUSTOMER_MERGE_GENERIC_FORBIDDEN:
    i18n.HTTP_ERRORS.CUSTOMER_MERGE_GENERIC_FORBIDDEN,
  PROVIDER_MERGE_INTO_SELF: i18n.HTTP_ERRORS.PROVIDER_MERGE_INTO_SELF,
  PROVIDER_MERGE_NOT_FOUND: i18n.HTTP_ERRORS.PROVIDER_MERGE_NOT_FOUND,
  CUSTOMER_DELETE_HAS_RECORDS: i18n.HTTP_ERRORS.CUSTOMER_DELETE_HAS_RECORDS,
  PROVIDER_DELETE_HAS_RECORDS: i18n.HTTP_ERRORS.PROVIDER_DELETE_HAS_RECORDS,

  // PR5 — per-entity GL routing
  EXPENSE_CATEGORY_NOT_FOUND: i18n.HTTP_ERRORS.EXPENSE_CATEGORY_NOT_FOUND,
  EXPENSE_CATEGORY_ACCOUNT_NOT_FOUND:
    i18n.HTTP_ERRORS.EXPENSE_CATEGORY_ACCOUNT_NOT_FOUND,
  EXPENSE_CATEGORY_ACCOUNT_NOT_POSTABLE:
    i18n.HTTP_ERRORS.EXPENSE_CATEGORY_ACCOUNT_NOT_POSTABLE,
  STOCK_ADJUST_REASON_NOT_FOUND: i18n.HTTP_ERRORS.STOCK_ADJUST_REASON_NOT_FOUND,
  STOCK_ADJUST_REASON_ACCOUNT_NOT_FOUND:
    i18n.HTTP_ERRORS.STOCK_ADJUST_REASON_ACCOUNT_NOT_FOUND,
  STOCK_ADJUST_REASON_ACCOUNT_NOT_POSTABLE:
    i18n.HTTP_ERRORS.STOCK_ADJUST_REASON_ACCOUNT_NOT_POSTABLE,
  ACCOUNTING_ROUTING_ACCOUNT_NOT_FOUND:
    i18n.HTTP_ERRORS.ACCOUNTING_ROUTING_ACCOUNT_NOT_FOUND,
  ACCOUNTING_ROUTING_ACCOUNT_NOT_POSTABLE:
    i18n.HTTP_ERRORS.ACCOUNTING_ROUTING_ACCOUNT_NOT_POSTABLE,
  JOURNAL_PREVIEW_NO_RULE: i18n.HTTP_ERRORS.JOURNAL_PREVIEW_NO_RULE,
  JOURNAL_PREVIEW_EVENT_NOT_FOUND:
    i18n.HTTP_ERRORS.JOURNAL_PREVIEW_EVENT_NOT_FOUND,
  JOURNAL_PREVIEW_FAILED: i18n.HTTP_ERRORS.JOURNAL_PREVIEW_FAILED,
  RULES_ENGINE_PRODUCT_CATEGORY_NOT_FOUND:
    i18n.HTTP_ERRORS.RULES_ENGINE_PRODUCT_CATEGORY_NOT_FOUND,
  RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_MISSING:
    i18n.HTTP_ERRORS.RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_MISSING,
  RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_INVALID:
    i18n.HTTP_ERRORS.RULES_ENGINE_PRODUCT_CATEGORY_CONFIG_INVALID,
  RULES_ENGINE_PRODUCT_CATEGORY_PATH_MISSING:
    i18n.HTTP_ERRORS.RULES_ENGINE_PRODUCT_CATEGORY_PATH_MISSING,
  RULES_ENGINE_EXPENSE_CATEGORY_NOT_FOUND:
    i18n.HTTP_ERRORS.RULES_ENGINE_EXPENSE_CATEGORY_NOT_FOUND,
  RULES_ENGINE_EXPENSE_CATEGORY_PATH_MISSING:
    i18n.HTTP_ERRORS.RULES_ENGINE_EXPENSE_CATEGORY_PATH_MISSING,
  RULES_ENGINE_STOCK_ADJUST_REASON_NOT_FOUND:
    i18n.HTTP_ERRORS.RULES_ENGINE_STOCK_ADJUST_REASON_NOT_FOUND,
  RULES_ENGINE_STOCK_ADJUST_REASON_PATH_MISSING:
    i18n.HTTP_ERRORS.RULES_ENGINE_STOCK_ADJUST_REASON_PATH_MISSING,

  // DGII compliance phase 1 + 2
  SUPPLIER_PAYMENT_ISR_AMOUNT_INVALID:
    i18n.HTTP_ERRORS.SUPPLIER_PAYMENT_ISR_AMOUNT_INVALID,
  SUPPLIER_PAYMENT_ISR_TYPE_MISSING:
    i18n.HTTP_ERRORS.SUPPLIER_PAYMENT_ISR_TYPE_MISSING,
  SUPPLIER_INVOICE_NOT_PAYABLE: i18n.HTTP_ERRORS.SUPPLIER_INVOICE_NOT_PAYABLE,
  SUPPLIER_PAYMENT_OVERPAYMENT: i18n.HTTP_ERRORS.SUPPLIER_PAYMENT_OVERPAYMENT,
  SUPPLIER_PAYMENT_ALREADY_VOIDED:
    i18n.HTTP_ERRORS.SUPPLIER_PAYMENT_ALREADY_VOIDED,
  SUPPLIER_PAYMENT_VOID_REASON_REQUIRED:
    i18n.HTTP_ERRORS.SUPPLIER_PAYMENT_VOID_REASON_REQUIRED,
  SUPPLIER_PAYMENT_DUPLICATE_REFERENCE:
    i18n.HTTP_ERRORS.SUPPLIER_PAYMENT_DUPLICATE_REFERENCE,
  SUPPLIER_PAYMENT_NOT_FOUND: i18n.HTTP_ERRORS.SUPPLIER_PAYMENT_NOT_FOUND,
  EXPENSE_NOT_FOUND: i18n.HTTP_ERRORS.EXPENSE_NOT_FOUND,
  EXPENSE_NOT_PAYABLE: i18n.HTTP_ERRORS.EXPENSE_NOT_PAYABLE,
  EXPENSE_HAS_PAYMENTS: i18n.HTTP_ERRORS.EXPENSE_HAS_PAYMENTS,
  EXPENSE_ALREADY_VOIDED: i18n.HTTP_ERRORS.EXPENSE_ALREADY_VOIDED,
  EXPENSE_VOID_REASON_REQUIRED: i18n.HTTP_ERRORS.EXPENSE_VOID_REASON_REQUIRED,
  EXPENSE_VOID_USE_ANNUL: i18n.HTTP_ERRORS.EXPENSE_VOID_USE_ANNUL,
  EXPENSE_CREDIT_PROVIDER_REQUIRED:
    i18n.HTTP_ERRORS.EXPENSE_CREDIT_PROVIDER_REQUIRED,
  SUPPLIER_INVOICE_PAYMENT_EXCEEDS_BALANCE:
    i18n.HTTP_ERRORS.SUPPLIER_INVOICE_PAYMENT_EXCEEDS_BALANCE,
  SUPPLIER_CREDIT_NOTE_NOT_RECEIVABLE:
    i18n.HTTP_ERRORS.SUPPLIER_CREDIT_NOTE_NOT_RECEIVABLE,
  SUPPLIER_CREDIT_NOTE_PARENT_CANCELLED:
    i18n.HTTP_ERRORS.SUPPLIER_CREDIT_NOTE_PARENT_CANCELLED,
  PURCHASE_ORDER_ALREADY_CANCELLED:
    i18n.HTTP_ERRORS.PURCHASE_ORDER_ALREADY_CANCELLED,
  INVOICE_ANNULMENT_REASON_INVALID:
    i18n.HTTP_ERRORS.INVOICE_ANNULMENT_REASON_INVALID,
  INVOICE_ECF_ALREADY_SUBMITTED: i18n.HTTP_ERRORS.INVOICE_ECF_ALREADY_SUBMITTED,
  INVOICE_ANNUL_NOT_PERMITTED: i18n.HTTP_ERRORS.INVOICE_ANNUL_NOT_PERMITTED,
  INVOICE_TYPE_PERMISSION_DENIED:
    i18n.HTTP_ERRORS.INVOICE_TYPE_PERMISSION_DENIED,
  NCF_EXHAUSTION_WARNING: i18n.HTTP_ERRORS.NCF_EXHAUSTION_WARNING,

  // AR write-off (manual balance forgiveness)
  INVOICE_NOT_FOUND: i18n.HTTP_ERRORS.INVOICE_NOT_FOUND,
  INVOICE_ALREADY_PAID: i18n.HTTP_ERRORS.INVOICE_ALREADY_PAID,
  INVOICE_TOTAL_CHANGED: i18n.HTTP_ERRORS.INVOICE_TOTAL_CHANGED,
  INVOICE_NOT_A_DRAFT: i18n.HTTP_ERRORS.INVOICE_NOT_A_DRAFT,
  INVOICE_NOT_A_DRAFT_USE_CREDIT_NOTE:
    i18n.HTTP_ERRORS.INVOICE_NOT_A_DRAFT_USE_CREDIT_NOTE,
  CONDUCE_NOT_A_CONDUCE: i18n.HTTP_ERRORS.CONDUCE_NOT_A_CONDUCE,
  RESTAURANT_ACCOUNT_PAYMENT_LOCKED:
    i18n.HTTP_ERRORS.RESTAURANT_ACCOUNT_PAYMENT_LOCKED,
  RESTAURANT_DISCOUNT_BOTH_MODES:
    i18n.HTTP_ERRORS.RESTAURANT_DISCOUNT_BOTH_MODES,
  CONDUCE_NOT_EDITABLE: i18n.HTTP_ERRORS.CONDUCE_NOT_EDITABLE,
  CONDUCE_ALREADY_CONVERTED: i18n.HTTP_ERRORS.CONDUCE_ALREADY_CONVERTED,
  CONDUCE_ALREADY_CANCELLED: i18n.HTTP_ERRORS.CONDUCE_ALREADY_CANCELLED,
  CONDUCE_RETURN_EXCEEDS_DISPATCHED:
    i18n.HTTP_ERRORS.CONDUCE_RETURN_EXCEEDS_DISPATCHED,
  INVOICE_NOT_ELIGIBLE_FOR_CREDIT_NOTE:
    i18n.HTTP_ERRORS.INVOICE_NOT_ELIGIBLE_FOR_CREDIT_NOTE,
  INVOICE_ALREADY_FINALIZED: i18n.HTTP_ERRORS.INVOICE_ALREADY_FINALIZED,
  INVOICE_HAS_PENDING_PAYMENTS: i18n.HTTP_ERRORS.INVOICE_HAS_PENDING_PAYMENTS,
  INVOICE_CANCELLED: i18n.HTTP_ERRORS.INVOICE_CANCELLED,
  INVOICE_NO_OUTSTANDING: i18n.HTTP_ERRORS.INVOICE_NO_OUTSTANDING,
  INVOICE_WRITE_OFF_EXCEEDS_OUTSTANDING:
    i18n.HTTP_ERRORS.INVOICE_WRITE_OFF_EXCEEDS_OUTSTANDING,
  INVOICE_WRITE_OFF_INVALID_AMOUNT:
    i18n.HTTP_ERRORS.INVOICE_WRITE_OFF_INVALID_AMOUNT,
  INVOICE_WRITE_OFF_INVALID_REASON:
    i18n.HTTP_ERRORS.INVOICE_WRITE_OFF_INVALID_REASON,
  INVOICE_WRITE_OFF_COMMENT_REQUIRED:
    i18n.HTTP_ERRORS.INVOICE_WRITE_OFF_COMMENT_REQUIRED,
  INVOICE_WRITE_OFF_COMMENT_TOO_LONG:
    i18n.HTTP_ERRORS.INVOICE_WRITE_OFF_COMMENT_TOO_LONG,
  INVOICE_WRITE_OFF_POST_FAILED: i18n.HTTP_ERRORS.INVOICE_WRITE_OFF_POST_FAILED,

  // Inventory catalog reset
  INVENTORY_RESET_INVALID_CONFIRMATION:
    i18n.HTTP_ERRORS.INVENTORY_RESET_INVALID_CONFIRMATION,
  INVENTORY_RESET_BRANCH_NOT_FOUND:
    i18n.HTTP_ERRORS.INVENTORY_RESET_BRANCH_NOT_FOUND,
  INVENTORY_RESET_HQ_MISSING: i18n.HTTP_ERRORS.INVENTORY_RESET_HQ_MISSING,

  // Operations wipe (delete-operations.sql runtime)
  OPERATIONS_RESET_INVALID_CONFIRMATION:
    i18n.HTTP_ERRORS.OPERATIONS_RESET_INVALID_CONFIRMATION,
  OPERATIONS_RESET_INVALID_ID: i18n.HTTP_ERRORS.OPERATIONS_RESET_INVALID_ID,

  // e-CF approval-commercial certification (Paso 3)
  ECF_APPROVAL_XLSX_WRONG_SHEET: i18n.HTTP_ERRORS.ECF_APPROVAL_XLSX_WRONG_SHEET,

  // Capability / quota (PR-10 / PR-11 / PR-12)
  QUOTA_EXCEEDED: i18n.HTTP_ERRORS.QUOTA_EXCEEDED,
  FEATURE_NOT_AVAILABLE: i18n.HTTP_ERRORS.FEATURE_NOT_AVAILABLE,
  CAPABILITY_DENIED: i18n.HTTP_ERRORS.CAPABILITY_DENIED,

  // Subscription billing (Phase 1-9)
  STRIPE_BILLING_UNAVAILABLE: i18n.HTTP_ERRORS.STRIPE_BILLING_UNAVAILABLE,
  STRIPE_PM_ATTACHED_TO_DIFFERENT_CUSTOMER:
    i18n.HTTP_ERRORS.STRIPE_PM_ATTACHED_TO_DIFFERENT_CUSTOMER,
  PLAN_NOT_BILLABLE: i18n.HTTP_ERRORS.PLAN_NOT_BILLABLE,
  CHANGE_APPLY_FAILED: i18n.HTTP_ERRORS.CHANGE_APPLY_FAILED,

  // Payment claims (customer-facing dispute flow)
  PAYMENT_CLAIM_REASON_REQUIRED: i18n.HTTP_ERRORS.PAYMENT_CLAIM_REASON_REQUIRED,
  PAYMENT_CLAIM_INVALID_AMOUNT: i18n.HTTP_ERRORS.PAYMENT_CLAIM_INVALID_AMOUNT,
  PAYMENT_CLAIM_PENDING_EXISTS: i18n.HTTP_ERRORS.PAYMENT_CLAIM_PENDING_EXISTS,
  PAYMENT_CLAIM_NOT_FOUND: i18n.HTTP_ERRORS.PAYMENT_CLAIM_NOT_FOUND,
  PAYMENT_CLAIM_ALREADY_REVIEWED:
    i18n.HTTP_ERRORS.PAYMENT_CLAIM_ALREADY_REVIEWED,
  PAYMENT_CLAIM_REJECT_REASON_REQUIRED:
    i18n.HTTP_ERRORS.PAYMENT_CLAIM_REJECT_REASON_REQUIRED,
  PAYMENT_CLAIM_NO_SUBSCRIPTION: i18n.HTTP_ERRORS.PAYMENT_CLAIM_NO_SUBSCRIPTION,

  SALES_ORDER_DELIVERY_DATE_REQUIRED:
    i18n.HTTP_ERRORS.SALES_ORDER_DELIVERY_DATE_REQUIRED,
  SALES_ORDER_PAID_IMMUTABLE: i18n.HTTP_ERRORS.SALES_ORDER_PAID_IMMUTABLE,
  SALES_ORDER_ITEM_LOCKED: i18n.HTTP_ERRORS.SALES_ORDER_ITEM_LOCKED,
  SALES_ORDER_HEADER_DISCOUNT_EXCEEDS_SUBTOTAL:
    i18n.HTTP_ERRORS.SALES_ORDER_HEADER_DISCOUNT_EXCEEDS_SUBTOTAL,
  SALES_ORDER_HAS_INVOICE: i18n.HTTP_ERRORS.SALES_ORDER_HAS_INVOICE,
  SALES_ORDER_SENT_TO_PRODUCTION:
    i18n.HTTP_ERRORS.SALES_ORDER_SENT_TO_PRODUCTION,
  SALES_ORDER_COMMANDA_STARTED: i18n.HTTP_ERRORS.SALES_ORDER_COMMANDA_STARTED,
  // e-CF batch processor
  ECF_INVOICE_NOT_FOUND: i18n.HTTP_ERRORS.ECF_INVOICE_NOT_FOUND,
  ECF_NCF_TYPE_UNMAPPED: i18n.HTTP_ERRORS.ECF_NCF_TYPE_UNMAPPED,
  ECF_NCF_NOT_ELECTRONIC: i18n.HTTP_ERRORS.ECF_NCF_NOT_ELECTRONIC,
  ECF_NOTE_MISSING_REFERENCE: i18n.HTTP_ERRORS.ECF_NOTE_MISSING_REFERENCE,
  DEBIT_NOTE_REQUIRES_ORIGINAL: i18n.HTTP_ERRORS.DEBIT_NOTE_REQUIRES_ORIGINAL,
  DEBIT_NOTE_ORIGINAL_NOT_FOUND: i18n.HTTP_ERRORS.DEBIT_NOTE_ORIGINAL_NOT_FOUND,
  NOTE_ORIGINAL_SERIES_MISMATCH: i18n.HTTP_ERRORS.NOTE_ORIGINAL_SERIES_MISMATCH,
  INVOICE_CANCELLED_NO_PAYMENT: i18n.HTTP_ERRORS.INVOICE_CANCELLED_NO_PAYMENT,
  ECF_NCF_SEQUENCE_EXHAUSTED: i18n.HTTP_ERRORS.ECF_NCF_SEQUENCE_EXHAUSTED,
  ECF_REPAIR_ALREADY_ACCEPTED: i18n.HTTP_ERRORS.ECF_REPAIR_ALREADY_ACCEPTED,
  ECF_REPAIR_PAID_INVOICE_DELTA: i18n.HTTP_ERRORS.ECF_REPAIR_PAID_INVOICE_DELTA,
  ECF_ACTIVATION_REQUIRES_CERTIFICATE:
    i18n.HTTP_ERRORS.ECF_ACTIVATION_REQUIRES_CERTIFICATE,
  ECF_CERTIFICATE_DGII_AUTH_FAILED:
    i18n.HTTP_ERRORS.ECF_CERTIFICATE_DGII_AUTH_FAILED,
  ECF_CERTIFICATE_PASSPHRASE_INVALID:
    i18n.HTTP_ERRORS.ECF_CERTIFICATE_PASSPHRASE_INVALID,
  ECF_CERTIFICATE_FILE_INVALID: i18n.HTTP_ERRORS.ECF_CERTIFICATE_FILE_INVALID,
  ECF_ENVIRONMENT_REQUIRES_CERTIFICATE:
    i18n.HTTP_ERRORS.ECF_ENVIRONMENT_REQUIRES_CERTIFICATE,

  // Pre-Factura per-surface control
  PRE_FACTURA_DISABLED_FOR_SURFACE:
    i18n.HTTP_ERRORS.PRE_FACTURA_DISABLED_FOR_SURFACE,
  PRE_FACTURA_NO_FISCAL_SEQUENCE:
    i18n.HTTP_ERRORS.PRE_FACTURA_NO_FISCAL_SEQUENCE,
  PRE_FACTURA_REQUIRES_FISCAL_SEQUENCE:
    i18n.HTTP_ERRORS.PRE_FACTURA_REQUIRES_FISCAL_SEQUENCE,

  // Per-branch access set (BranchAccessService)
  BRANCH_ACCESS_DENIED: i18n.HTTP_ERRORS.BRANCH_ACCESS_DENIED,
  USER_BRANCH_ACCESS_REQUIRED: i18n.HTTP_ERRORS.USER_BRANCH_ACCESS_REQUIRED,
  ALL_BRANCHES_ACCESS_DENIED: i18n.HTTP_ERRORS.ALL_BRANCHES_ACCESS_DENIED,
  USER_BRANCH_ACCESS_INVALID: i18n.HTTP_ERRORS.USER_BRANCH_ACCESS_INVALID,
  USER_SELF_BRANCH_ACCESS_FORBIDDEN:
    i18n.HTTP_ERRORS.USER_SELF_BRANCH_ACCESS_FORBIDDEN,

  // Tax catalog — default-tax invariants
  TAX_DEFAULT_CANNOT_DELETE: i18n.HTTP_ERRORS.TAX_DEFAULT_CANNOT_DELETE,
  TAX_DEFAULT_MISSING: i18n.HTTP_ERRORS.TAX_DEFAULT_MISSING,
  TAX_SERVICE_CHARGE_CANNOT_DELETE:
    i18n.HTTP_ERRORS.TAX_SERVICE_CHARGE_CANNOT_DELETE,
  TAX_RATE_CHANGE_BLOCKED_ECF: i18n.HTTP_ERRORS.TAX_RATE_CHANGE_BLOCKED_ECF,
  TAX_RATE_DUPLICATE: i18n.HTTP_ERRORS.TAX_RATE_DUPLICATE,
  TAX_CANONICAL_RATE_IMMUTABLE: i18n.HTTP_ERRORS.TAX_CANONICAL_RATE_IMMUTABLE,

  // Reseller platform
  RESELLER_USER_NOT_ACCEPTED_YET:
    i18n.HTTP_ERRORS.RESELLER_USER_NOT_ACCEPTED_YET,
  RESELLER_USER_DISABLED: i18n.HTTP_ERRORS.RESELLER_USER_DISABLED,

  // Recipes / raw materials
  RECIPE_INGREDIENT_MUST_BE_STOCK_TRACKED:
    i18n.RECIPES.INGREDIENT_NOT_STOCK_TRACKED,
  RECIPE_CANNOT_REFERENCE_SELF: i18n.RECIPES.CANNOT_REFERENCE_SELF,
  RECIPE_INGREDIENT_CANNOT_BE_RECIPE: i18n.RECIPES.INGREDIENT_CANNOT_BE_RECIPE,
  RECIPE_INGREDIENT_UNIT_MISMATCH_PHASE_1:
    i18n.RECIPES.INGREDIENT_UNIT_MISMATCH,
  RECIPE_INGREDIENT_NOT_FOUND: i18n.RECIPES.INGREDIENT_NOT_FOUND,
  RECIPE_COST_EXCEEDS_PRICE_WARN: i18n.RECIPES.COST_EXCEEDS_PRICE_WARN,
  RECIPE_COST_EXCEEDS_PRICE_BLOCKED: i18n.RECIPES.COST_EXCEEDS_PRICE_BLOCKED,

  // Procurement PO
  PROCUREMENT_PO_NO_ITEMS: i18n.HTTP_ERRORS.PROCUREMENT_PO_NO_ITEMS,
  PROCUREMENT_PO_BRANCH_REQUIRED:
    i18n.HTTP_ERRORS.PROCUREMENT_PO_BRANCH_REQUIRED,

  ROLE_PERMISSION_BRANCH_REQUIRED:
    i18n.HTTP_ERRORS.ROLE_PERMISSION_BRANCH_REQUIRED,

  // Generic errors
  INTERNAL_SERVER_ERROR: i18n.HTTP_ERRORS.INTERNAL_SERVER_ERROR,
  BAD_REQUEST: i18n.HTTP_ERRORS.BAD_REQUEST,
  FORBIDDEN: i18n.HTTP_ERRORS.FORBIDDEN,
  NOT_FOUND: i18n.HTTP_ERRORS.NOT_FOUND,
};

/**
 * Error response from backend. Additional structured fields (e.g.
 * `rate`, `taxId`, `code`) are pass-through — they land as i18n
 * interpolation vars when the mapped translation includes `{{name}}`
 * placeholders.
 *
 * Deliberately does NOT declare a `[key: string]: unknown;` index
 * signature — that would break every legacy caller that passes a
 * plain `Error` instance (no index signature on `Error`). We read
 * arbitrary fields via a typed narrowing inside the utility instead.
 */
interface ErrorResponse {
  errorCode?: string;
  message?: string;
  statusCode?: number;
}

/**
 * Translation function signature compatible with react-i18next `t`.
 * The optional second arg is the interpolation object — matches
 * `{{var}}` placeholders in the translation string. Kept structural
 * so utility can be called with `useTranslation().t` OR a bare wrapper.
 */
export type TranslateFn = (
  key: string,
  params?: Record<string, unknown>
) => string;

/**
 * Get translated error message based on error code or message.
 * Prefers errorCode mapping over message text matching. Any extra
 * fields on the error payload flow into the translation as
 * interpolation vars — e.g. `TAX_RATE_DUPLICATE` carries `rate` and
 * the Spanish translation reads
 * `"Ya existe un impuesto con la tasa {{rate}}%..."`.
 *
 * @param error - The error object from the backend (with errorCode
 *   + any structured context fields the FE should surface).
 * @param t - The translation function from useTranslation().
 * @returns The translated error message or original message.
 */
export const getTranslatedErrorMessage = (
  error: ErrorResponse | string | undefined,
  t: TranslateFn
): string | undefined => {
  if (!error) {
    return undefined;
  }

  // If error is a string, return it as is
  if (typeof error === 'string') {
    return error;
  }

  // If error is an object with errorCode, use that for mapping
  const errorRecord = error as Record<string, unknown>;
  const errorCode = errorRecord.errorCode as string | undefined;
  const mappedKey = errorCode
    ? errorCodeMap[errorCode as ErrorCode]
    : undefined;
  if (mappedKey) {
    // Pass the whole error payload as interpolation vars — react-i18next
    // silently ignores keys that don't match any `{{var}}` placeholder,
    // so extra fields cost nothing but let structured backend context
    // (rate, code, taxId, count, …) flow into the operator-facing toast
    // without a per-code branch.
    //
    // ApiError shape (from api.service.createApiError) nests the raw
    // backend body under `.details`; flatten it here so a mapped code
    // like TAX_RATE_DUPLICATE picks up its `{{rate}}` field regardless
    // of whether the caller passed the wrapped ApiError or the raw
    // response body.
    const detailsCandidate = errorRecord.details;
    const details =
      typeof detailsCandidate === 'object' && detailsCandidate !== null
        ? (detailsCandidate as Record<string, unknown>)
        : {};
    // HTTP filter forwards structured context under `data` — flatten
    // it so `{{rate}}` / `{{taxId}}` / etc. resolve without a caller
    // having to know whether the field came in as a top-level key or
    // via the filter-blessed `data` bag. Applies to both ApiError.details
    // (wrapped) AND a raw response body passed directly.
    const detailsData = details.data;
    const flattenedDetailsData =
      typeof detailsData === 'object' && detailsData !== null
        ? (detailsData as Record<string, unknown>)
        : {};
    const rootData = errorRecord.data;
    const flattenedRootData =
      typeof rootData === 'object' && rootData !== null
        ? (rootData as Record<string, unknown>)
        : {};
    const params: Record<string, unknown> = {
      ...details,
      ...flattenedDetailsData,
      ...flattenedRootData,
    };
    for (const [key, value] of Object.entries(errorRecord)) {
      if (
        key === 'errorCode' ||
        key === 'statusCode' ||
        key === 'details' ||
        key === 'data' ||
        key === 'message'
      ) {
        continue;
      }
      params[key] = value;
    }
    return t(mappedKey, params);
  }

  // Fall back to original message if errorCode not found
  if (error.message) {
    return error.message;
  }

  return undefined;
};

/**
 * Category-key → i18n label for the "contact has related records" delete guard.
 * Keys mirror the backend `pending` breakdown emitted by
 * customers/providers `remove()`.
 */
const DELETE_PENDING_LABELS: Record<string, string> = {
  invoices: i18n.CONTACTS_DELETE.PENDING_INVOICES,
  refunds: i18n.CONTACTS_DELETE.PENDING_REFUNDS,
  salesOrders: i18n.CONTACTS_DELETE.PENDING_SALES_ORDERS,
  creditTransactions: i18n.CONTACTS_DELETE.PENDING_CREDIT_TRANSACTIONS,
  openTabs: i18n.CONTACTS_DELETE.PENDING_OPEN_TABS,
  storeCredit: i18n.CONTACTS_DELETE.PENDING_STORE_CREDIT,
  purchaseOrders: i18n.CONTACTS_DELETE.PENDING_PURCHASE_ORDERS,
  procurementOrders: i18n.CONTACTS_DELETE.PENDING_PROCUREMENT_ORDERS,
  expenses: i18n.CONTACTS_DELETE.PENDING_EXPENSES,
  payments: i18n.CONTACTS_DELETE.PENDING_PAYMENTS,
  taxDocuments: i18n.CONTACTS_DELETE.PENDING_TAX_DOCUMENTS,
};

/** Read the `pending` breakdown wherever the HTTP pipeline parked it. */
const extractPending = (
  error: Record<string, unknown>
): Record<string, number> | undefined => {
  const candidates: unknown[] = [error.pending];
  const details = error.details;
  if (typeof details === 'object' && details !== null) {
    const d = details as Record<string, unknown>;
    candidates.push(d.pending);
    if (typeof d.data === 'object' && d.data !== null) {
      candidates.push((d.data as Record<string, unknown>).pending);
    }
  }
  const data = error.data;
  if (typeof data === 'object' && data !== null) {
    candidates.push((data as Record<string, unknown>).pending);
  }
  for (const c of candidates) {
    if (typeof c === 'object' && c !== null) {
      return c as Record<string, number>;
    }
  }
  return undefined;
};

/**
 * Build the operator-facing message for the customer/provider delete guard
 * (`*_DELETE_HAS_RECORDS`). Lists the pending categories with their counts so
 * the operator knows exactly what blocks the delete (and that merge is the way
 * out). Returns null when the error is not a delete-guard error.
 */
export const getDeletePendingMessage = (
  error: unknown,
  t: TranslateFn
): string | null => {
  if (typeof error !== 'object' || error === null) return null;
  const errorRecord = error as Record<string, unknown>;
  const code =
    (errorRecord.errorCode as string | undefined) ??
    (typeof errorRecord.details === 'object' && errorRecord.details !== null
      ? ((errorRecord.details as Record<string, unknown>).errorCode as
          | string
          | undefined)
      : undefined);
  if (
    code !== 'CUSTOMER_DELETE_HAS_RECORDS' &&
    code !== 'PROVIDER_DELETE_HAS_RECORDS'
  ) {
    return null;
  }

  const pending = extractPending(errorRecord);
  const base =
    code === 'CUSTOMER_DELETE_HAS_RECORDS'
      ? t(i18n.HTTP_ERRORS.CUSTOMER_DELETE_HAS_RECORDS)
      : t(i18n.HTTP_ERRORS.PROVIDER_DELETE_HAS_RECORDS);
  if (!pending) return base;

  const parts = Object.entries(pending)
    .filter(([key, n]) => n > 0 && DELETE_PENDING_LABELS[key])
    .map(([key, n]) =>
      key === 'storeCredit'
        ? t(DELETE_PENDING_LABELS[key])
        : `${n} ${t(DELETE_PENDING_LABELS[key])}`
    );
  if (parts.length === 0) return base;
  return `${base} (${parts.join(', ')})`;
};
